import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import { styled, css } from '@mui/material/styles';
import React from 'react';

import Button from '~/client/components/base/button';
import Chip from '~/client/components/base/chip';
import { getDesignTokens } from '~/client/components/base/core';
import {
  GenericCard,
  GenericCardHeader,
  GenericCardContent,
  GenericCardActions,
} from '~/client/components/base/generic-card';
import Text from '~/client/components/base/text';
import {
  aiSystemAssessmentReportRouteUtil,
  functionalModelAssessmentReportRouteUtil,
  candidateModelAssessmentReportRouteUtil,
  aiDashboardUtils,
  functionalModelDashboardUtils,
  candidateModelDashboardUtils,
} from '~/client/routes';
import { formatAssignees, getStatusLabel } from '~/client/utils/task-helpers';
import * as GQL from '~/graphql/generated/client';

interface TaskCardProps {
  task: GQL.UserTask;
  onStart: (task: GQL.UserTask) => void;
  onCommentClick: (task: GQL.UserTask) => void;
  className?: string;
  isArchived?: boolean;
}

const TaskCardImpl: React.FC<TaskCardProps> = ({
  task,
  onStart,
  onCommentClick,
  className,
  isArchived = false,
}) => {
  const commentCount = task.commentCount || 0;

  const aiSystemReportHelper = aiSystemAssessmentReportRouteUtil.useHelper();
  const functionalModelReportHelper =
    functionalModelAssessmentReportRouteUtil.useHelper();
  const candidateModelReportHelper =
    candidateModelAssessmentReportRouteUtil.useHelper();

  const aiDashboard = aiDashboardUtils.useHelper();
  const functionalModelDashboard = functionalModelDashboardUtils.useHelper();
  const candidateModelDashboard = candidateModelDashboardUtils.useHelper();

  const handleStart = () => {
    try {
      switch (task.projectType) {
        case GQL.ProjectType.Organization:
        case GQL.ProjectType.AiSystem:
          if (task.assignmentType === GQL.AssignmentType.Submission) {
            return aiDashboard.navigateTo('DASHBOARD', {
              params: {
                projectId: task.projectId,
              },
            });
          }
          aiSystemReportHelper.navigateTo('ASSESSMENT_REPORT', {
            params: {
              projectId: task.projectId,
              assessmentId: task.assessmentId,
            },
            state: {
              from: 'tasks',
            },
          });
          break;

        case GQL.ProjectType.FunctionalModel:
          if (task.parentAISystem) {
            if (task.assignmentType === GQL.AssignmentType.Submission) {
              return functionalModelDashboard.navigateTo('DASHBOARD', {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.projectId,
                },
              });
            }
            functionalModelReportHelper.navigateTo('ASSESSMENT_REPORT', {
              params: {
                projectId: task.parentAISystem,
                functionalModelId: task.projectId,
                assessmentId: task.assessmentId,
              },
              state: {
                from: 'tasks',
              },
            });
          }
          break;

        case GQL.ProjectType.ModelCandidate:
        case GQL.ProjectType.ModelChampion:
        case GQL.ProjectType.Dataset:
          if (task.parentAISystem && task.functionalModel) {
            if (task.assignmentType === GQL.AssignmentType.Submission) {
              return candidateModelDashboard.navigateTo('CANDIDATE_DETAILS', {
                params: {
                  projectId: task.parentAISystem,
                  functionalModelId: task.functionalModel,
                  candidateId: task.projectId,
                },
              });
            }
            candidateModelReportHelper.navigateTo('ASSESSMENT_REPORT', {
              params: {
                projectId: task.parentAISystem,
                functionalModelId: task.functionalModel,
                candidateId: task.projectId,
                assessmentId: task.assessmentId,
              },
              state: {
                from: 'tasks',
              },
            });
          }
          break;
      }
    } catch (err) {
      console.error('Error navigating to task details:', err);
    }

    // Call the original onStart callback if provided
    onStart(task);
  };

  return (
    <GenericCard variant="minimal" className={className}>
      <GenericCardHeader
        title={<Text sz="title">{task.policyName}</Text>}
        action={
          <div className="header-chips">
            {task.versionNumber && (
              <Chip
                className="version-chip"
                label={<Text sz="body14">{task.versionNumber}</Text>}
                variant="filled"
                size="small"
              />
            )}
            <Chip
              className="type-chip"
              label={
                <Text sz="body14">{task.assignmentType.toLowerCase()}</Text>
              }
              variant="filled"
            />
          </div>
        }
      />
      <GenericCardContent>
        <div className="task-info">
          <Text sz="body14" color="secondary">
            {task.assessmentName}
          </Text>
          <Text sz="body14" color="secondary">
            {task.projectName} ({task.projectTypeLabel})
          </Text>
          <Text sz="body14" color="secondary">
            Assigned to: {formatAssignees(task.assignees || [])}
          </Text>
          {isArchived ? (
            <Text sz="body14" color="secondary">
              Archived:{' '}
              {task.archivedAt
                ? new Date(task.archivedAt).toLocaleDateString()
                : '-'}
            </Text>
          ) : (
            <Text sz="body14" color="secondary">
              Due Date: {new Date(task.dueDate).toLocaleDateString()}
            </Text>
          )}
          <div className="comment-section">
            <IconButton
              size="small"
              onClick={() => onCommentClick(task)}
              className="comment-btn"
            >
              <Badge
                badgeContent={commentCount}
                color="primary"
                invisible={
                  commentCount === 0 ||
                  (isArchived
                    ? (task.overallOutcome ?? task.status) ===
                      GQL.AssignmentStatusType.Pending
                    : task.status === GQL.AssignmentStatusType.Pending)
                }
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.65rem',
                    height: '16px',
                    minWidth: '16px',
                    color: 'white',
                  },
                }}
              >
                <ChatBubbleOutlineIcon fontSize="small" />
              </Badge>
            </IconButton>
            <Text sz="body14" color="secondary">
              Comments
            </Text>
          </div>
        </div>
      </GenericCardContent>
      <GenericCardActions alignment="space-between">
        {isArchived ? (
          <Chip
            className="outcome-chip"
            data-outcome={task.overallOutcome ?? task.status}
            label={
              <Text sz="body14">
                {getStatusLabel(task.overallOutcome ?? task.status)}
              </Text>
            }
            variant="filled"
          />
        ) : (
          <>
            <Chip
              className="status-chip"
              data-status={task.status}
              label={<Text sz="body14">{getStatusLabel(task.status)}</Text>}
              variant="filled"
            />
            <Button onClick={handleStart}>Start</Button>
          </>
        )}
      </GenericCardActions>
    </GenericCard>
  );
};

const TaskCard = styled(TaskCardImpl)(({ theme }) => {
  const t = getDesignTokens(theme.palette.mode, theme.accentPalette);
  return css`
    /* Override border color to be darker */
    &.MuiCard-root {
      border: 1px solid ${t.borderSubtle};
      width: 100%;
      height: 100%;
    }

    .task-info {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing(1)};
    }

    .comment-section {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
      margin-top: ${theme.spacing(1)};
    }

    .comment-btn {
      color: ${t.iconInteractive};
      &:hover {
        background-color: ${t.surface2};
      }
    }

    .header-chips {
      display: flex;
      gap: ${theme.spacing(1)};
      align-items: center;
    }

    .type-chip {
      background-color: ${t.accentBg};
      color: ${t.accent};
    }

    .version-chip {
      background-color: ${t.accentBg};
      color: ${t.accent};
    }

    .status-chip {
      &[data-status='PENDING'] {
        background-color: ${t.surface2};
        color: ${t.textPrimary};
      }
      &[data-status='COMPLETED'] {
        background-color: ${theme.palette.success.main};
        color: ${t.status.success};
      }
      &[data-status='REJECTED'] {
        background-color: ${t.risk.highBg};
        color: ${t.status.danger};
      }
    }

    .outcome-chip {
      background-color: ${t.surface2};
      color: ${t.textPrimary};
      text-transform: capitalize;

      &[data-outcome='REJECTED'] {
        background-color: ${t.risk.highBg};
        color: ${t.status.danger};
      }
      &[data-outcome='COMPLETED'] {
        background-color: ${theme.palette.success.main};
        color: ${t.status.success};
      }
    }
  `;
});

export default TaskCard;
